import { createApp, nextTick } from 'vue/dist/vue.esm-bundler';
import App from './App.vue';
import router from './router';
import store from './store';
import MasterLayout from './components/MasterLayout.vue';
import { createLogoutUri, redirectDeepLinkPath } from './utils/redirect-uri';
import VueSocialSharing from 'vue-social-sharing';
import VueShepherd from 'vue-shepherd';
import { Browser } from '@capacitor/browser';
import Vueform from '@vueform/vueform';
import vueformConfig from './../vueform.config';
import { SafeArea } from '@capacitor-community/safe-area';

import { isNativeNotWeb, isIosNativeOnly } from './utils/platform-check';
import { initConsoleLogger } from './utils/console-logger';
import { initMixPanel } from './utils/mixpanel';
import { setupOneSignalUser } from './utils/onesignal-setup';

import { IonicVue } from '@ionic/vue';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';
import 'tailwindcss/base.css';
import 'tailwindcss/components.css';
import 'tailwindcss/utilities.css';

/* Theme variables */
import './theme/variables.css';

/* TailwindCSS */
import './styles/main.scss';

import { auth0 } from './utils/auth0';
import HtmlToVue from './components/common/HtmlToVue.vue';
import {
  initDatadogLogs,
  initDatadogRum,
  addDatadogRumError,
  applyReplaySamplingDecision,
} from './utils/datadog';
import { getAppVersion } from './utils/app-version';
import OneSignal from 'onesignal-cordova-plugin';
import InAppBrowser from './components/InAppBrowser.vue';
import youtubePlayer from './utils/youtube-player';
import i18n from './utils/i18n';

(async () => {
  // Initialize console logger to forward logs to Android Logcat
  initConsoleLogger();

  initMixPanel();

  try {
    if (isNativeNotWeb()) {
      await SafeArea.initialize();
    }
  } catch (error) {
    console.error('Error initializing SafeArea plugin:', error);
  }

  const app = createApp(App)
    .use(IonicVue)
    .use(auth0)
    .use(router)
    .use(store)
    .use(VueSocialSharing)
    .use(VueShepherd)
    .use(youtubePlayer)
    .use(Vueform, vueformConfig)
    .use(i18n)
    .component('master-layout', MasterLayout)
    .component('html-to-vue', HtmlToVue)
    .component('in-app-browser', InAppBrowser);

  // Pre-fetch app version so it's available for Datadog initialization
  // This ensures we get the fresh version from Capacitor (important after app updates)
  // The version will be cached, so getAppVersionSync() will use the fresh value
  getAppVersion()
    .then(() => {
      // Initialize Datadog after version is fetched to ensure correct version is used
      setTimeout(() => {
        try {
          initDatadogLogs();
          initDatadogRum();
        } catch (error) {
          console.error('[Datadog] Initialization failed:', error);
        }
      }, 0);
    })
    .catch(() => {
      // If version fetch fails, still initialize Datadog with fallback version
      setTimeout(() => {
        try {
          initDatadogLogs();
          initDatadogRum();
        } catch (error) {
          console.error('[Datadog] Initialization failed:', error);
        }
      }, 0);
    });

  router.isReady().then(() => {
    // Same trigger point as the old unconditional startSessionReplayRecording(),
    // but gated by the backend-controlled rate (sysparams DATADOG_REPLAY_SAMPLE_RATE,
    // a '0'-'100' string). Cap the wait at 500ms so a slow/offline boot never
    // delays the replay decision — on timeout we proceed with the default
    // (undefined -> fail open to 100). applyReplaySamplingDecision decides once
    // per run, so a late sysparams response after the timeout is ignored.
    const REPLAY_DECISION_TIMEOUT_MS = 500;
    const decideReplaySampling = () => {
      const rate = store
        .dispatch('sysparams/fetchParams')
        .then(params => params?.DATADOG_REPLAY_SAMPLE_RATE)
        .catch(() => undefined);
      const timeout = new Promise(resolve =>
        setTimeout(() => resolve(undefined), REPLAY_DECISION_TIMEOUT_MS),
      );
      return Promise.race([rate, timeout]).then(r => applyReplaySamplingDecision(r));
    };

    if (process.env.VUE_APP_NODE_ENV === 'local') {
      const token = localStorage.getItem('token');
      if (token) store.commit('user/setToken', token);
      app.mount('#app');
      nextTick(() => decideReplaySampling());
      return;
    }

    app.mount('#app');
    nextTick(() => decideReplaySampling());
    if (isNativeNotWeb() && process.env.VUE_APP_ONESIGNAL_API_KEY) {
      OneSignal.initialize(process.env.VUE_APP_ONESIGNAL_API_KEY);
      OneSignal.Notifications.requestPermission();
      OneSignal.Notifications.addEventListener('click', async event => {
        console.log('The notification was clicked!', event);

        const isInternal = await redirectDeepLinkPath(router, event.notification.launchURL, store);

        if (!isInternal && isIosNativeOnly()) {
          await Browser.open({ url: event.notification.launchURL });
        }
      });

      // Setup OneSignal user when profile is loaded
      let oneSignalUserSetup = false;

      const performOneSignalSetup = profileData => {
        if (oneSignalUserSetup || !profileData || !profileData.id) {
          return;
        }
        oneSignalUserSetup = true;
        setupOneSignalUser(profileData);
      };

      // Check if profile is already loaded
      if (store.state.profile && store.state.profile.id) {
        performOneSignalSetup(store.state.profile);
      }

      // Watch for future profile loads
      store.subscribe((mutation, state) => {
        if (mutation.type === 'profile/setProfileState') {
          performOneSignalSetup(state.profile);
        }
      });
    }
  });

  app.config.errorHandler = async function (err, vm, info) {
    if (process.env.VUE_APP_NODE_ENV !== 'production') {
      console.log('in error handler');
      console.error(err);
    }

    addDatadogRumError(err, {
      vueInfo: info,
      componentName: vm?.$options?.name ?? vm?.$options?.__name ?? 'unknown',
    });

    if (err.message === 'Request failed with status code 401') {
      // Drop cached sysparams so a tester's preview values don't linger for the
      // next account on a shared device.
      store.commit('sysparams/clearParams');
      await auth0.logout({
        logoutParams: {
          returnTo: createLogoutUri(
            process.env.VUE_APP_PACKAGE_ID,
            process.env.VUE_APP_AUTH0_DOMAIN,
          ),
        },
        openUrl: async url =>
          await Browser.open({
            url,
            windowName: '_self',
          }),
      });
    }
  };

  window.onerror = function (message, source, lineno, colno, error) {
    if (process.env.VUE_APP_NODE_ENV !== 'production') {
      console.log('in window error handler');
      console.log(message);
      console.error(error);
    }
    addDatadogRumError(error || new Error(String(message)), {
      source,
      lineno,
      colno,
    });
  };

  window.addEventListener('unhandledrejection', async event => {
    if (process.env.VUE_APP_NODE_ENV !== 'production') {
      console.log('in unhandled rejecction');
      console.error(event);
      console.log(event.reason?.message);
    }

    const err =
      event.reason instanceof Error
        ? event.reason
        : new Error(event.reason?.message ?? String(event.reason ?? 'Unhandled rejection'));
    addDatadogRumError(err, {
      reason: event.reason?.message,
    });

    if (
      event.reason &&
      event.reason.message &&
      (event.reason.message === 'Request failed with status code 401' ||
        event.reason.message === 'Login required' ||
        event.reason.message.startsWith('Missing Refresh Token') ||
        event.reason.message.startsWith('Unknown or invalid refresh token'))
    ) {
      // Drop cached sysparams so a tester's preview values don't linger for the
      // next account on a shared device.
      store.commit('sysparams/clearParams');
      await auth0.logout({
        logoutParams: {
          returnTo: createLogoutUri(
            process.env.VUE_APP_PACKAGE_ID,
            process.env.VUE_APP_AUTH0_DOMAIN,
          ),
        },
        openUrl: async url =>
          await Browser.open({
            url,
            windowName: '_self',
          }),
      });
    }
  });
})();
